import { onBeforeUnmount, onMounted, ref } from 'vue'
import { api } from '@/lib/api'
import { isNewConversation } from '@/lib/support'

const unanswered = ref(0)
const notice = ref<string | null>(null)
function storedChime(): boolean {
  try {
    return window.localStorage.getItem('oshela.support.chime') !== 'off'
  } catch {
    return true
  }
}

const chime = ref(storedChime())

/** The chime preference, for the inbox's toggle; reading it starts no polling. */
export const supportChime = chime
let seen = -1

/**
 * The panel's only alert for support (owner decision 2026-10-05: no SMS): the sidebar badge,
 * a notice when a new conversation arrives, the tab title, and an optional short chime.
 */
export function useSupportAlerts(enabled: () => boolean) {
  let timer: ReturnType<typeof setInterval> | undefined
  const untitled = (): string => document.title.replace(/^\(\d+\) /, '')

  function beep(): void {
    if (!chime.value) return

    try {
      const audio = new AudioContext()
      const tone = audio.createOscillator()
      const gain = audio.createGain()
      tone.frequency.value = 880
      gain.gain.setValueAtTime(0.12, audio.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + 0.4)
      tone.connect(gain).connect(audio.destination)
      tone.start()
      tone.stop(audio.currentTime + 0.4)
    } catch {
      // No audio before the first click on the page; the badge and notice still show.
    }
  }

  async function check(): Promise<void> {
    // The admin's permissions arrive with the profile, after the layout mounts; ask each tick.
    if (document.hidden || !enabled()) return

    try {
      const response = await api.get<{ data: { unanswered: number; latest: number } }>('/support/unread')
      const { unanswered: count, latest } = response.data.data

      unanswered.value = count
      document.title = count > 0 ? `(${count}) ${untitled()}` : untitled()

      if (seen >= 0 && isNewConversation(latest, seen)) {
        notice.value = 'گفت‌وگوی تازه با پشتیبانی رسید.'
        beep()
      }

      seen = Math.max(seen, latest)
    } catch {
      // Offline or signed out: the next tick tries again.
    }
  }

  function dismiss(): void {
    notice.value = null
  }

  onMounted(() => {
    void check()
    timer = setInterval(check, 15_000)
  })

  onBeforeUnmount(() => {
    clearInterval(timer)
    document.title = untitled()
  })

  return { unanswered, notice, chime, dismiss, check }
}

export function setChime(on: boolean): void {
  chime.value = on
  try {
    window.localStorage.setItem('oshela.support.chime', on ? 'on' : 'off')
  } catch {
    // Private mode: the choice lasts as long as the tab.
  }
}
